import { useState } from 'react';
import { API } from '../global/global';

interface CertificacionPresencialData {
  hash_documento: string;
  qr_code: string;
  url_verificacion: string;
  fecha_certificacion: string;
  numero_certificacion: string;
  modulos_certificados: string[];
  integridad_valida?: boolean;
}

interface CertificacionPresencialResponse {
  success: boolean;
  message: string;
  data?: CertificacionPresencialData;
  error?: string;
}

export const useCertificacionPresencial = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generarCertificacion = async (evaluadoId: string): Promise<CertificacionPresencialData | null> => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/certificaciones-presenciales/generar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ evaluado_id: evaluadoId }),
      });

      const result: CertificacionPresencialResponse = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Error al generar certificación presencial');
      }

      if (result.success && result.data) {
        return result.data;
      } else {
        throw new Error(result.message || 'Error desconocido');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error generando certificación presencial:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const obtenerCertificacion = async (evaluadoId: string): Promise<CertificacionPresencialData | null> => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/certificaciones-presenciales/${evaluadoId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result: CertificacionPresencialResponse = await response.json();

      if (!response.ok) {
        if (response.status === 404) {
          return null; // No existe certificación
        }
        throw new Error(result.message || 'Error al obtener certificación presencial');
      }

      if (result.success && result.data) {
        return result.data;
      } else {
        throw new Error(result.message || 'Error desconocido');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error obteniendo certificación presencial:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const descargarQR = (qrCode: string, nombreEvaluado: string) => {
    try {
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${qrCode}`;
      link.download = `QR_Certificacion_Presencial_${nombreEvaluado.replace(/\s+/g, '_')}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error descargando QR:', err);
      setError('Error al descargar el código QR');
    }
  };

  const regenerarCertificacion = async (evaluadoId: string): Promise<CertificacionPresencialData | null> => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/certificaciones-presenciales/regenerar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ evaluado_id: evaluadoId }),
      });

      const result: CertificacionPresencialResponse = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Error al regenerar certificación presencial');
      }

      if (result.success && result.data) {
        return result.data;
      } else {
        throw new Error(result.message || 'Error desconocido');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error regenerando certificación presencial:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    generarCertificacion,
    obtenerCertificacion,
    regenerarCertificacion,
    descargarQR,
    loading,
    error,
    clearError: () => setError(null),
  };
};
